import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * Tracks how many columns fit across an element by measuring its own width via
 * ResizeObserver, instead of leaving the column count to CSS `@media (min-width)`
 * breakpoints. Two consumers need this instead of a plain CSS grid:
 *
 * - `features/campaigns/components/shared/VirtualizedGamesGrid`: the column count also
 *   drives the virtualizer's row count and row slicing, so it has to be measured in JS.
 * - `features/search/components/SearchSection`: `.searchContainer` is `position: fixed`,
 *   and on iOS Safari a fixed-position element's box can keep a stale width across a
 *   portrait -> landscape -> portrait cycle even though the viewport-based media query
 *   itself re-evaluates correctly. Measuring the actual box sidesteps that.
 *
 * Returns a callback ref rather than taking a `RefObject`: a component that stays
 * permanently mounted and renders `null` while inactive (e.g. SearchSection) won't have
 * its target div in the DOM the first time a plain `useRef`-driven effect would run - and
 * since a ref object's identity never changes, that effect would never fire again once the
 * div actually mounts. A callback ref re-invokes on every attach, so the observer always
 * gets (re)attached to the real node, which works for both mount patterns.
 */
export const useMeasuredColumnCount = (
  getColumnCount: (width: number) => number,
  initialColumnCount: number,
): [number, (node: HTMLDivElement | null) => void] => {
  const [columnCount, setColumnCount] = useState(initialColumnCount);
  const [node, setNode] = useState<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!node) return undefined;

    let rafId = 0;
    const measure = () => {
      const width = node.clientWidth;
      // Keep the last known count on a zero width rather than collapsing to whatever
      // getColumnCount(0) would give us.
      if (width > 0) setColumnCount(getColumnCount(width));
    };

    // Deferring to the next frame avoids the "ResizeObserver loop completed with
    // undelivered notifications" warning when our state update causes layout changes
    // that re-trigger the observer synchronously.
    const scheduleMeasure = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    measure();

    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(node);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [node, getColumnCount]);

  const setRef = useCallback((el: HTMLDivElement | null) => {
    setNode(el);
  }, []);

  return [columnCount, setRef];
};
